import type { Camera } from "../../components/singletons";
import type { BabylonState, ToolHover, ToolInput } from "../../state";

import { pickScenePoint } from "./pickScenePoint";
import { projectDataPoint } from "./projectDataPoint";

/**
 * Tells React what the measure tool is over, at most once per change.
 *
 * Runs after `scene.render`, against the world matrices that frame just
 * wrote. Pointer events are not a valid trigger: their rate is unbounded
 * and they see last frame's matrices. A frame with the same cursor over
 * the same scene reports nothing and picks nothing. A hover ending
 * (background, pointer off-canvas, tool off) reports `null` exactly once.
 * No-op for a state built without tool input or a manager built without
 * an `onToolHover` callback; the ingest preview's manager has none.
 *
 * One hover feeds both halves of the overlay: the coordinate readout, and
 * the marker showing which point the next click would pin.
 */
export function reportToolHover(state: BabylonState): void {
  const tool = state.input.tool;
  const onToolHover = state.context.callbacks.onToolHover;
  if (!tool || !onToolHover) {
    return;
  }

  // Absent when the tool is off or the pointer is off the canvas; either
  // one ends the hover rather than freezing it.
  const pointer = tool.mode === "measure" ? tool.pointer : undefined;
  const camera = state.sceneConfig.camera;

  let hover: ToolHover | null = null;
  if (pointer && camera) {
    if (!hoverPickInputsChanged(state, tool, camera, pointer)) {
      // Nothing the pick depends on has changed, so the last report
      // stands. Re-deriving it would cost a full `scene.pick` plus a
      // scan of every cloud vertex and path sample.
      return;
    }
    const pick = pickScenePoint(state, pointer.x, pointer.y);
    if (pick) {
      const projected = projectDataPoint(state, camera, pick.dataPoint);
      hover = {
        dataPoint: pick.dataPoint,
        screenX: pointer.x,
        screenY: pointer.y,
        pickScreenX: projected.screenX,
        pickScreenY: projected.screenY,
        sampleTime: pick.sampleTime,
      };
    }
  } else {
    // Forget the recorded inputs so the next frame with a pointer picks
    // again. Otherwise turning the tool off and straight back on with a
    // still cursor would skip the pick that restores the hover this
    // frame is about to clear.
    tool.hoverPickInputs = undefined;
  }

  const previous = tool.reportedHover ?? null;
  if (hoverEquals(previous, hover)) {
    return;
  }
  tool.reportedHover = hover;
  onToolHover(hover);
}

/** Slot layout of the packed hover-pick inputs; see {@link hoverPickInputsChanged}. */
const POINTER_X = 0;
const POINTER_Y = 1;
const FOCUS_X = 2;
const FOCUS_Y = 3;
const FOCUS_Z = 4;
const ENTITY_GENERATION = 5;
const TIME = 6;
const VIEW_MATRIX = 7;
const INPUT_COUNT = VIEW_MATRIX + 16;

/**
 * Reused so the gate costs comparisons rather than a per-frame allocation.
 * Safe to share across managers: it is filled and read within one
 * synchronous call.
 */
const scratch = new Float64Array(INPUT_COUNT);

/**
 * Whether anything the pick's answer depends on has moved since the last one,
 * recording the current values when it has.
 *
 * What is packed in:
 * - the cursor, which is the question being asked;
 * - the view matrix, which covers every camera move;
 * - `camera.focusPoint`, because `applyRenderOrigin` shifts every mesh by
 *   it. In orbit mode it tracks the followed entity, so geometry moves
 *   under a still cursor while the view matrix does not.
 * - `entityGeneration`, bumped whenever the scene's content changes (an
 *   entity added or removed, a mesh or path finishing its load);
 * - the current time, which walks entities along their tracks during
 *   playback. Held as a double: at epoch-nanosecond magnitudes that rounds
 *   to ~256ns, far finer than a frame.
 *
 * Not covered: joints driven imperatively through `setJointPose` or
 * `setModelJointPose`, which move geometry without touching any of these.
 * Their only caller is the ingest preview, whose manager has no
 * `onToolHover` callback, so no hover there can go stale.
 */
function hoverPickInputsChanged(
  state: BabylonState,
  tool: ToolInput,
  camera: Camera,
  pointer: { x: number; y: number },
): boolean {
  const { sceneConfig } = state;
  scratch[POINTER_X] = pointer.x;
  scratch[POINTER_Y] = pointer.y;
  scratch[FOCUS_X] = camera.focusPoint.x;
  scratch[FOCUS_Y] = camera.focusPoint.y;
  scratch[FOCUS_Z] = camera.focusPoint.z;
  scratch[ENTITY_GENERATION] = sceneConfig.entityGeneration ?? 0;
  scratch[TIME] = Number(sceneConfig.time ?? 0n);
  const view = state.context.scene.getViewMatrix().m;
  for (let i = 0; i < 16; i += 1) {
    scratch[VIEW_MATRIX + i] = view[i];
  }

  const previous = tool.hoverPickInputs;
  if (!previous) {
    tool.hoverPickInputs = Float64Array.from(scratch);
    return true;
  }
  for (let i = 0; i < INPUT_COUNT; i += 1) {
    if (previous[i] !== scratch[i]) {
      previous.set(scratch);
      return true;
    }
  }
  return false;
}

/**
 * Compared field for field because every frame builds a fresh hover object,
 * so identity would report sixty times a second. Exact float equality is
 * enough: unchanged inputs re-derive the same floats.
 *
 * The screen clause is not redundant with the data-point clause. On a point
 * cloud every cursor position inside the snap radius resolves to the same
 * vertex, so without it the cursor-following readout would freeze at the
 * position of the first snap.
 */
function hoverEquals(a: ToolHover | null, b: ToolHover | null): boolean {
  if (a === null || b === null) {
    return a === b;
  }
  return (
    a.screenX === b.screenX &&
    a.screenY === b.screenY &&
    a.pickScreenX === b.pickScreenX &&
    a.pickScreenY === b.pickScreenY &&
    a.dataPoint.x === b.dataPoint.x &&
    a.dataPoint.y === b.dataPoint.y &&
    a.dataPoint.z === b.dataPoint.z &&
    // A stationary entity can hold one position across many samples, so two
    // hits can agree on every coordinate and still be different moments.
    a.sampleTime === b.sampleTime
  );
}
